import React, { useState } from 'react';
import { Column as ColumnType, Card as CardType, cardApi } from '../services/api';
import { Card } from './Card';
import { ConfirmModal } from './ConfirmModal';
import { Check, Xmark, Plus } from 'iconoir-react';

interface ColumnProps {
  column: ColumnType;
  boardId: number;
  onUpdate: (column: ColumnType) => void;
  onDelete: (columnId: number) => void;
  onCardMove: (card: CardType, fromColumnId: number, toColumnId: number) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, columnId: number) => void;
  onDragStart: (card: CardType) => void;
  onDragEnd: () => void;
  isDragOver: boolean;
  onRefresh: () => void;
  onColumnDragStart?: (column: ColumnType) => void;
  onColumnDragEnd?: () => void;
  onColumnDrop?: (e: React.DragEvent, targetColumn: ColumnType) => void;
  isColumnDragOver?: boolean;
  userRole?: string;
  cardIdToOpen?: number | null;
}

export const Column: React.FC<ColumnProps> = ({
  column,
  boardId,
  onUpdate,
  onDelete,
  onCardMove,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragStart,
  onDragEnd,
  isDragOver,
  onRefresh,
  onColumnDragStart,
  onColumnDragEnd,
  onColumnDrop,
  isColumnDragOver = false,
  userRole = 'visitor',
  cardIdToOpen = null
}) => {
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [newCardTitle, setNewCardTitle] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState(column.title);
  const [dragOverCardId, setDragOverCardId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const isAdmin = userRole === 'admin';
  const canReorderColumns = userRole === 'admin' || userRole === 'editor';

  const handleAddCard = async () => {
    if (newCardTitle.trim()) {
      try {
        await cardApi.create(column.id, {
          title: newCardTitle.trim(),
          position: column.cards.length
        });
        setNewCardTitle('');
        setIsAddingCard(false);
        onRefresh();
      } catch (err) {
        console.error('Erreur lors de la création de la carte', err);
      }
    }
  };

  const handleDeleteCard = async (cardId: number) => {
    try {
      await cardApi.delete(column.id, cardId);
      onRefresh();
    } catch (err) {
      console.error('Erreur lors de la suppression de la carte', err);
    }
  };

  const handleUpdateCard = async (updatedCard: CardType) => {
    try {
      await cardApi.update(column.id, updatedCard.id, {
        title: updatedCard.title,
        description: updatedCard.description,
        label_color: updatedCard.label_color
      });
      onRefresh();
    } catch (err) {
      console.error('Erreur lors de la mise à jour de la carte', err);
    }
  };

  const handleArchiveCard = async (cardId: number, archived: boolean) => {
    try {
      await cardApi.update(column.id, cardId, {
        archived: archived
      });
      onRefresh();
    } catch (err) {
      console.error('Erreur lors de l\'archivage de la carte', err);
    }
  };

  const handleSaveTitle = () => {
    if (editTitle.trim()) {
      onUpdate({ ...column, title: editTitle.trim() });
      setIsEditingTitle(false);
    }
  };

  const handleCancelTitle = () => {
    setEditTitle(column.title);
    setIsEditingTitle(false);
  };

  const handleCardDragOver = (e: React.DragEvent, cardId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCardId(cardId);
  };

  const handleCardDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCardId(null);
  };

  const handleCardDrop = async (e: React.DragEvent, targetCard: CardType) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCardId(null);
    
    // On récupère la carte en cours de déplacement depuis le dataTransfer
    const draggedCardData = e.dataTransfer.getData('card');
    if (!draggedCardData) return;
    
    const draggedCard: CardType = JSON.parse(draggedCardData);
    
    // Si c'est la même carte, on ne fait rien
    if (draggedCard.id === targetCard.id) return;
    
    // Calculer la nouvelle position
    const targetPosition = targetCard.position;
    
    try {
      // Si c'est dans la même colonne, on réordonne
      if (draggedCard.column_id === column.id) {
        await cardApi.update(column.id, draggedCard.id, {
          position: targetPosition
        });
      } else {
        // Si c'est une autre colonne, on déplace
        await cardApi.move(draggedCard.id, {
          column_id: column.id,
          position: targetPosition
        });
      }
      onRefresh();
    } catch (err) {
      console.error('Erreur lors du déplacement de la carte', err);
    }
  };

  const handleColumnDragStart = (e: React.DragEvent) => {
    if (canReorderColumns && onColumnDragStart) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('column', JSON.stringify(column));
      onColumnDragStart(column);
    }
  };

  const handleColumnDragEnd = () => {
    if (onColumnDragEnd) {
      onColumnDragEnd();
    }
  };

  const handleColumnDragOver = (e: React.DragEvent) => {
    if (canReorderColumns) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleColumnDrop = (e: React.DragEvent) => {
    if (canReorderColumns && onColumnDrop) {
      e.preventDefault();
      e.stopPropagation();
      onColumnDrop(e, column);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    onDelete(column.id);
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <div
        className={`kanban-column ${isDragOver ? 'drag-over' : ''} ${isColumnDragOver ? 'column-drag-over' : ''}`}
        onDragOver={(e) => {
          onDragOver(e);
          handleColumnDragOver(e);
        }}
        onDragLeave={onDragLeave}
        onDrop={(e) => {
          onDrop(e, column.id);
          handleColumnDrop(e);
        }}
      >
        <div className="kanban-column-header">
          {canReorderColumns && (
            <div 
              className="column-drag-handle" 
              title="Glisser pour déplacer la colonne"
              draggable={true}
              onDragStart={handleColumnDragStart}
              onDragEnd={handleColumnDragEnd}
            >
              ⋮⋮
            </div>
          )}
          {isEditingTitle ? (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1 }}>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  color: 'white',
                  fontSize: '14px',
                  flex: 1
                }}
              />
              <button onClick={handleSaveTitle} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>
                <Check width={18} height={18} strokeWidth={2} />
              </button>
              <button onClick={handleCancelTitle} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>
                <Xmark width={18} height={18} strokeWidth={2} />
              </button>
            </div>
          ) : (
            <div onClick={() => setIsEditingTitle(true)} style={{ cursor: 'pointer', flex: 1 }}>
              {column.title}
            </div>
          )}
          {isAdmin && (
            <button
              className="delete-btn"
              onClick={handleDeleteClick}
              style={{ color: 'white' }}
              title="Supprimer la colonne"
            >
              <Xmark width={20} height={20} strokeWidth={2} />
            </button>
          )}
        </div>

      <div>
        {column.cards.filter(card => !card.archived).map(card => (
          <div
            key={card.id}
            onDragOver={(e) => handleCardDragOver(e, card.id)}
            onDragLeave={handleCardDragLeave}
            onDrop={(e) => handleCardDrop(e, card)}
            className={dragOverCardId === card.id ? 'card-drop-zone' : ''}
          >
            <Card
              card={card}
              onUpdate={handleUpdateCard}
              onDelete={handleDeleteCard}
              onArchive={handleArchiveCard}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              initiallyOpen={cardIdToOpen === card.id}
              userRole={userRole}
              boardId={boardId}
              onRefresh={onRefresh}
            />
          </div>
        ))}

        {isAddingCard ? (
          <div className="card-form">
            <textarea
              value={newCardTitle}
              onChange={(e) => setNewCardTitle(e.target.value)}
              placeholder="Titre de la carte..."
              maxLength={144}
              autoFocus
            />
            <div className="card-form-buttons">
              <button className="btn btn-primary" onClick={handleAddCard}>
                Ajouter
              </button>
              <button className="btn btn-cancel" onClick={() => {
                setIsAddingCard(false);
                setNewCardTitle('');
              }}>
                Annuler
              </button>
            </div>
          </div>
        ) : (
          <button className="add-card-btn" onClick={() => setIsAddingCard(true)}>
            <Plus width={16} height={16} strokeWidth={2} />
            <span>Ajouter une carte</span>
          </button>
        )}
      </div>
    </div>

    <ConfirmModal
      isOpen={showDeleteConfirm}
      title="Supprimer la colonne"
      message={`Êtes-vous sûr de vouloir supprimer la colonne "${column.title}" ? Toutes les cartes qu'elle contient seront également supprimées.`}
      confirmText="Supprimer"
      cancelText="Annuler"
      onConfirm={handleConfirmDelete}
      onCancel={() => setShowDeleteConfirm(false)}
      isDangerous={true}
    />
  </>
  );
};
